import { type CSSProperties, type KeyboardEvent, type ReactNode } from 'react'
import { MessageSquare } from 'lucide-react'
import { cellLabel, type CellAnchor } from './cellAnchor'

// The comment count shown right before a planned number on the review page.
// Renders nothing for a cell without comments.
export function CommentCount({ count }: { count: number }) {
  if (count === 0) return null
  return (
    <span className="inline-flex items-center gap-0.5 rounded-full bg-primary px-1.5 py-0.5 text-[11px] font-semibold leading-none text-primary-foreground" title={`${count} comment${count === 1 ? '' : 's'}`}>
      <MessageSquare size={11} strokeWidth={2.5} aria-hidden />{count}
    </span>
  )
}

export interface CellMarkState { count: number }

// Wraps a cell's planned number with its comment count right before it.
export function CellMark({ mark, children }: { mark?: CellMarkState; children: ReactNode }) {
  if (!mark || mark.count === 0) return <>{children}</>
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap">
      <CommentCount count={mark.count} />{children}
    </span>
  )
}

// A planned number on the review page. With onCellAction the whole cell is a
// button that opens its comments under the row; `selected` outlines the cell
// whose comments are open. Without onCellAction it is a plain cell, so the
// recap and version pages are unchanged.
export default function CommentableCell({ cell, onCellAction, selected = false, className, style, colSpan, title, children }: {
  cell: CellAnchor
  onCellAction?: (c: CellAnchor) => void
  selected?: boolean
  className: string
  style?: CSSProperties
  colSpan?: number
  title?: string
  children: ReactNode
}) {
  const outline = selected ? ' shadow-[inset_0_0_0_2px_var(--primary)]' : ''
  if (!onCellAction) {
    return <td className={className + outline} style={style} colSpan={colSpan} title={title}>{children}</td>
  }
  return (
    <td
      className={`${className}${outline} cursor-pointer hover:bg-primary/5`}
      style={style}
      colSpan={colSpan}
      title={title ?? `Comments on ${cellLabel(cell)}`}
      role="button"
      tabIndex={0}
      onClick={() => onCellAction(cell)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          if (e.key === ' ') e.preventDefault()
          onCellAction(cell)
        }
      }}
    >
      {children}
    </td>
  )
}

// Props that make a whole table row open one cell's comments: used where a
// row has a single commentable number (Overall Plan, and Channel Quarterly
// Breakdown when one quarter is planned). Undefined without an action.
export function rowClickProps(title: string, action: (() => void) | undefined) {
  if (!action) return undefined
  return {
    role: 'button',
    tabIndex: 0,
    title,
    onClick: action,
    onKeyDown: (e: KeyboardEvent<HTMLTableRowElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        if (e.key === ' ') e.preventDefault()
        action()
      }
    },
  }
}
